import { gql } from '@apollo/client';
import React, { useMemo, useState, useCallback } from 'react';
import classNames from 'classnames';
import { useParams, Link } from 'react-router-dom';
import { TrunkshowCellFragment } from '../../../../generated/types/TrunkshowCellFragment';
import { hrefFor } from '../../../../routers';
import { compact } from '../../../../lib/compact';
import { ImageGallery } from '../../../ImageGallery';
import { RelativeTime } from '../../../RelativeTime';
import './TrunkshowCell.scss';

export const TRUNKSHOW_CELL_FRAGMENT = gql`
  fragment TrunkshowCellFragment on Trunkshow {
    id
    slug
    name
    activeEndAt: active_end_i
    primaryImageUrls: primary_image_urls {
      src: main
    }
    previewImageUrls: preview_image_urls
  }
`;

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  trunkshow: TrunkshowCellFragment;
}

export const TrunkshowCell: React.FC<Props> = ({ className, trunkshow }) => {
  const { vertical = 'women' } = useParams<{ vertical?: string }>();
  const [hovered, setHovered] = useState(false);

  const href = hrefFor.TrunkshowPage({
    vertical: vertical,
    trunkshowId: trunkshow.id,
    trunkshowSlug: trunkshow.slug || trunkshow.id
  });

  const imageSrcs = useMemo(
    () => compact([trunkshow.primaryImageUrls?.src, ...trunkshow.previewImageUrls]),
    [trunkshow]
  );

  const images = useMemo(() => imageSrcs.map(src => ({ src, title: trunkshow.name })), [
    imageSrcs,
    trunkshow
  ]);

  const handleMouseEnter = useCallback(() => setHovered(true), []);
  const handleMouseLeave = useCallback(() => setHovered(false), []);

  return (
    <Link
      className={classNames('TrunkshowCell', className)}
      to={href || ''}
      data-pk={trunkshow.id}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <ImageGallery images={images} active={hovered} maxHeight={540} maxWidth={340} />

      {trunkshow.activeEndAt && trunkshow.name && (
        <div className="TrunkshowCell__overlay">
          <RelativeTime className="TrunkshowCell__timestamp" timestamp={trunkshow.activeEndAt}>
            Left
          </RelativeTime>

          <h3 className="TrunkshowCell__title">{trunkshow.name}</h3>
        </div>
      )}
    </Link>
  );
};
